import { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { EntityTypeEnum, InternalCommentOutDTO, PageResponseInternalCommentOutDTO, Pageable } from '@api/ringil-api.v2';

// Comments load 10 at a time (newest first); each "load more" appends the next 10 up to total.
const PAGE_SIZE = 10;

/**
 * Loads and mutates internal comments for a single entity (e.g. an expedition).
 * Page-number based paging (pageNumber + pageSize) — the backend ignores `offset`.
 * Comments are returned newest-first; loadMore() appends the next page below.
 */
export const useInternalComments = (entityType: EntityTypeEnum, entityId: string) => {
  const { listInternalComments, createInternalComment, updateInternalComment, deleteInternalComment } = useContext(ApiContext);

  const [comments, setComments] = useState<InternalCommentOutDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // number of pages currently loaded; the next loadMore fetches page index === loadedPages
  const loadedPages = useRef(0);

  const hasMore = comments.length < total;

  const fetchPage = (pageNumber: number, pageSize: number, onDone: (data: PageResponseInternalCommentOutDTO) => void) => {
    if (!entityId) return;
    setLoading(true);
    const page: Pageable = { pageNumber, pageSize, sortField: 'createdDate', sortDirection: 'DESC' };
    listInternalComments(
      entityType,
      entityId,
      page,
      data => {
        onDone(data);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          setLoading(false);
          showStandardError();
        },
      },
    );
  };

  // pageNumber is 1-based (page 1 === first page).
  const loadInitial = () => {
    loadedPages.current = 0;
    fetchPage(1, PAGE_SIZE, data => {
      setComments(data.data ?? []);
      setTotal(data.total ?? 0);
      loadedPages.current = 1;
    });
  };

  const loadMore = () => {
    const next = loadedPages.current + 1;
    fetchPage(next, PAGE_SIZE, data => {
      setComments(prev => [...prev, ...(data.data ?? [])]);
      setTotal(data.total ?? 0);
      loadedPages.current = next;
    });
  };

  // After a mutation, refetch the whole loaded window as a single first page so the
  // list neither collapses nor loses its place; keeps page-number alignment for loadMore.
  const reloadLoaded = () => {
    const pages = Math.max(1, loadedPages.current);
    fetchPage(1, pages * PAGE_SIZE, data => {
      setComments(data.data ?? []);
      setTotal(data.total ?? 0);
      loadedPages.current = pages;
    });
  };

  const create = (content: string, onDone?: () => void) => {
    createInternalComment(
      { entityType, entityId, content },
      () => {
        reloadLoaded();
        onDone?.();
      },
      null,
      { onError: (_error, showStandardError) => { showStandardError(); } },
    );
  };

  const update =(commentId: string, content: string, onDone?: () => void) => {
    updateInternalComment(
      commentId,
      { content },
      () => {
        reloadLoaded();
        onDone?.();
      },
      null,
      { onError: (_error, showStandardError) => { showStandardError(); } },
    );
  };

  const remove =(commentId: string, onDone?: () => void) => {
    deleteInternalComment(
      commentId,
      () => {
        reloadLoaded();
        onDone?.();
      },
      null,
      { onError: (_error, showStandardError) => { showStandardError(); } },
    );
  };

  return { comments, total, loading, hasMore, loadInitial, loadMore, create, update, remove };
};

export default useInternalComments;